import { useState, useEffect } from "react";
import { Routes, Route, Link, useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { NativeSelect } from "@/components/ui/native-select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Plus, ArrowLeft, DollarSign, TrendingUp, TrendingDown, AlertTriangle, PieChart } from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";

const costCategories = [
  { value: "labor", label: "Mão de Obra" },
  { value: "materials", label: "Materiais" },
  { value: "subcontractors", label: "Terceiros" },
  { value: "insurance", label: "Seguros" },
  { value: "other", label: "Outros" }
];

function FinancialOverview() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [summary, setSummary] = useState(null);
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchFinancialData();
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].id);
      }
    } catch (error) {
      toast.error("Erro ao carregar projetos");
    } finally {
      setLoading(false);
    }
  };

  const fetchFinancialData = async () => {
    try {
      const [summaryRes, costsRes] = await Promise.all([
        axios.get(`${API}/projects/${selectedProject}/financial-summary`),
        axios.get(`${API}/internal-costs?project_id=${selectedProject}`)
      ]);
      setSummary(summaryRes.data);
      setCosts(costsRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados financeiros");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luvcorp-navy"></div>
      </div>
    );
  }

  const getCategoryLabel = (category) => {
    const c = costCategories.find(cat => cat.value === category);
    return c ? c.label : category;
  };

  return (
    <div className="space-y-6" data-testid="financial-page">
      {/* Confidential Banner */}
      <div className="confidential-banner text-white px-4 py-2 rounded-lg flex items-center justify-between">
        <span className="font-bold text-sm tracking-wider">PAINEL INTERNO - CONFIDENCIAL</span>
        <AlertTriangle className="h-5 w-5" />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">Gestão Financeira</h1>
        <div className="flex gap-2">
          <NativeSelect
            value={selectedProject}
            onValueChange={setSelectedProject}
            placeholder="Selecione o projeto"
            options={projects.map(p => ({ value: p.id, label: p.name }))}
            className="w-64"
            data-testid="select-project"
          />
          <Link to="/financial/new-cost">
            <Button className="bg-luvcorp-gold hover:bg-luvcorp-gold-dark text-white" data-testid="new-cost-btn">
              <Plus className="h-4 w-4 mr-2" />
              Novo Custo
            </Button>
          </Link>
        </div>
      </div>

      {summary && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Valor do Contrato</p>
                    <p className="text-2xl font-bold text-luvcorp-navy">
                      R$ {summary.contract_value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <DollarSign className="h-8 w-8 text-luvcorp-gold" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Custos Totais</p>
                    <p className="text-2xl font-bold text-red-600">
                      R$ {summary.total_costs?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <TrendingDown className="h-8 w-8 text-red-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Margem</p>
                    <p className={`text-2xl font-bold ${summary.margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      R$ {summary.margin?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <TrendingUp className={`h-8 w-8 ${summary.margin >= 0 ? 'text-emerald-500' : 'text-red-500'}`} />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">% Margem</p>
                    <p className={`text-2xl font-bold ${summary.margin_percentage >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {summary.margin_percentage?.toFixed(1)}%
                    </p>
                  </div>
                  <PieChart className="h-8 w-8 text-luvcorp-navy" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Costs by Category */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-luvcorp-gold" />
                  Custos por Categoria
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {summary.costs_by_category && Object.entries(summary.costs_by_category).length > 0 ? (
                  Object.entries(summary.costs_by_category).map(([category, amount]) => (
                    <div key={category} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="font-medium">{getCategoryLabel(category)}</span>
                      <span className="text-luvcorp-navy font-bold">
                        R$ {amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">Nenhum custo registrado</p>
                )}
              </CardContent>
            </Card>

            {/* Cost History */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-heading">Histórico de Custos</CardTitle>
              </CardHeader>
              <CardContent>
                {costs.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Categoria</TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {costs.slice(0, 10).map((cost) => (
                        <TableRow key={cost.id}>
                          <TableCell className="text-sm">{new Date(cost.date).toLocaleDateString('pt-BR')}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{getCategoryLabel(cost.category)}</Badge>
                          </TableCell>
                          <TableCell className="text-sm truncate max-w-[150px]">{cost.description}</TableCell>
                          <TableCell className="text-right font-medium">
                            R$ {cost.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-8">Nenhum custo registrado</p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function NewInternalCost() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  
  const [formData, setFormData] = useState({
    project_id: "",
    category: "",
    description: "",
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    vendor: "",
    invoice_number: ""
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.log("Error:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/internal-costs`, formData);
      toast.success("Custo registrado com sucesso!");
      navigate("/financial");
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro ao registrar custo"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="new-internal-cost">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/financial">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">Novo Custo Interno</h1>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label>Projeto *</Label>
              <NativeSelect
                value={formData.project_id}
                onValueChange={(value) => setFormData({...formData, project_id: value})}
                placeholder="Selecione o projeto"
                options={projects.map(p => ({ value: p.id, label: p.name }))}
                data-testid="select-project"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categoria *</Label>
                <NativeSelect
                  value={formData.category}
                  onValueChange={(value) => setFormData({...formData, category: value})}
                  placeholder="Selecione"
                  options={costCategories.map(c => ({ value: c.value, label: c.label }))}
                  data-testid="select-category"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">Data *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required
                  data-testid="input-date"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição *</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Ex: Compra de cimento para fundação"
                required
                data-testid="input-description"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount">Valor (R$) *</Label>
              <Input
                id="amount"
                type="number"
                min="0"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value) || 0})}
                required
                data-testid="input-amount"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vendor">Fornecedor</Label>
                <Input
                  id="vendor"
                  value={formData.vendor}
                  onChange={(e) => setFormData({...formData, vendor: e.target.value})}
                  placeholder="Nome do fornecedor"
                  data-testid="input-vendor"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invoice_number">Nº da Nota Fiscal</Label>
                <Input
                  id="invoice_number"
                  value={formData.invoice_number}
                  onChange={(e) => setFormData({...formData, invoice_number: e.target.value})}
                  placeholder="Ex: NF-12345"
                  data-testid="input-invoice"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-luvcorp-navy hover:bg-luvcorp-navy-dark text-white h-11"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? "Registrando..." : "Registrar Custo"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default function FinancialPage() {
  return (
    <MainLayout>
      <Routes>
        <Route index element={<FinancialOverview />} />
        <Route path="new-cost" element={<NewInternalCost />} />
      </Routes>
    </MainLayout>
  );
}
